import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { api } from "@/api/client";
import { unwrap, type ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type { components } from "@/api/schema";
import type { MeResponse } from "@/api/types";

export type { MeResponse } from "@/api/types";
type S = components["schemas"];

export function useMe(): UseQueryResult<MeResponse, ApiError> {
  return useQuery<MeResponse, ApiError>({
    queryKey: queryKeys.me,
    queryFn: async () => unwrap(await api.GET("/auth/me")),
    staleTime: 5 * 60 * 1000,
    retry: false,
  });
}

export function useLogin() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiError, S["LoginRequest"]>({
    mutationFn: async (body) => unwrap(await api.POST("/auth/login", { body })),
    onSuccess: (me) => {
      qc.clear();
      qc.setQueryData(queryKeys.me, me);
    },
  });
}

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return useMutation<void, ApiError, void>({
    mutationFn: async () => {
      // Signing out locally must succeed even if the server call fails.
      await api.POST("/auth/logout").catch(() => undefined);
    },
    onSettled: () => {
      qc.clear();
      navigate("/login", { replace: true });
    },
  });
}

export function useAcceptInvite() {
  const qc = useQueryClient();
  return useMutation<MeResponse, ApiError, S["AcceptInviteRequest"]>({
    mutationFn: async (body) => unwrap(await api.POST("/auth/accept-invite", { body })),
    onSuccess: async () => {
      qc.clear();
      await qc.invalidateQueries({ queryKey: queryKeys.me });
    },
  });
}

export function useForgotPassword() {
  return useMutation<unknown, ApiError, S["ForgotPasswordRequest"]>({
    mutationFn: async (body) => unwrap(await api.POST("/auth/forgot-password", { body })),
  });
}

export function useResetPassword() {
  return useMutation<unknown, ApiError, S["ResetPasswordRequest"]>({
    mutationFn: async (body) => unwrap(await api.POST("/auth/reset-password", { body })),
  });
}

export function useChangePassword() {
  return useMutation<unknown, ApiError, S["ChangePasswordRequest"]>({
    mutationFn: async (body) => unwrap(await api.POST("/auth/change-password", { body })),
  });
}

export function useSignup() {
  return useMutation<unknown, ApiError, S["SignupRequest"]>({
    mutationFn: async (body) => unwrap(await api.POST("/auth/signup", { body })),
  });
}
